import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { setWeekAbsence } from '../api/client'
import { ABSENCE_KINDS, absenceHours } from './weekAbsences'

/**
 * The vacation and leave columns of a historic week.
 *
 * They sit beside the project columns because that is how the sheet reads, but
 * they are stored apart: putting them in `weekly_allocations` under an invented
 * project id would corrupt every hours-by-project report in the system.
 *
 * Editable only once the week has been saved. An open week is recomputed from
 * the Time off calendar every time it is read, so an edit here would be undone
 * without warning -- refusing is kinder than silently losing it.
 */
export default function WeekAbsenceCells({ week, data, memberId, onChanged }) {
  const { t } = useTranslation('engineering')
  const [editing, setEditing] = useState(null)
  const [value, setValue] = useState('')
  const frozen = !!data?.frozen

  const commit = async (kind) => {
    const hours = parseFloat(value) || 0
    setEditing(null)
    setValue('')
    if (hours === absenceHours(data, memberId, kind)) return
    try {
      await setWeekAbsence(week, { team_member_id: memberId, kind, hours })
      onChanged?.()
    } catch {
      onChanged?.()   // reload rather than leave a number that was not stored
    }
  }

  return ABSENCE_KINDS.map(kind => {
    const hours = absenceHours(data, memberId, kind)
    const isEditing = editing === kind
    return (
      <td key={kind} className="border border-gray-200 px-2 py-1.5 text-center bg-gray-50/50">
        {!frozen ? (
          <span className="text-gray-400"
                title={t('editWeek.absenceDerived')}>
            {hours > 0 ? `${hours}h` : '—'}
          </span>
        ) : isEditing ? (
          <input
            autoFocus
            type="number"
            min="0"
            max="80"
            step="0.5"
            className="w-20 text-center border border-gray-400 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
            value={value}
            onChange={e => setValue(e.target.value)}
            onBlur={() => commit(kind)}
            onKeyDown={e => {
              if (e.key === 'Enter') commit(kind)
              if (e.key === 'Escape') { setEditing(null); setValue('') }
            }}
          />
        ) : (
          <button
            onClick={() => { setEditing(kind); setValue(hours === 0 ? '' : String(hours)) }}
            title={t('editWeek.absenceEditable')}
            className={`w-20 text-center rounded-lg px-2 py-1 text-sm transition-colors border ${
              hours > 0
                ? 'bg-amber-50 border-amber-200 text-amber-800 font-semibold hover:bg-amber-100'
                : 'bg-white border-gray-200 text-gray-300 hover:border-gray-400 hover:text-gray-500'
            }`}
          >
            {hours > 0 ? `${hours}h` : t('editWeek.addHoursBtn')}
          </button>
        )}
      </td>
    )
  })
}
